import { useParams, Link, Navigate } from "@/lib/router-compat";
import { Helmet } from "react-helmet-async";
import DOMPurify from "dompurify";
import SiteHeader from "@/components/SiteHeader";
import VideoBanner, { SidebarVideoBanners } from "@/components/blackbox/VideoBanner";
import { blogArticles, getArticleBySlug, getRelatedArticles, legacyBlogSlugs } from "@/data/blogArticles";
import { Clock, Tag, ArrowLeft, ArrowRight, Share2, BookOpen } from "lucide-react";

const BlogArticle = () => {
  const { slug } = useParams<{ slug: string }>();

  // Redirect old English slugs to new pt-BR slugs (301-style)
  if (slug && legacyBlogSlugs[slug]) {
    return <Navigate to={`/blog/${legacyBlogSlugs[slug]}`} replace />;
  }

  const article = slug ? getArticleBySlug(slug) : undefined;

  if (!article) {
    return <Navigate to="/blog" replace />;
  }

  const relatedArticles = getRelatedArticles(slug!, 3);

  // Schema markup for article
  const articleSchema = {
    "@context": "https://schema.org",
    "@type": "Article",
    "headline": article.title,
    "description": article.metaDescription,
    "author": {
      "@type": "Organization",
      "name": "Método P-nis Liso"
    },
    "publisher": {
      "@type": "Organization",
      "name": "Método P-nis Liso",
      "logo": {
        "@type": "ImageObject",
        "url": "https://smothmethod.com/logo.jpg"
      }
    },
    "datePublished": article.publishedAt,
    "dateModified": article.updatedAt,
    "mainEntityOfPage": {
      "@type": "WebPage",
      "@id": `https://smothmethod.com/blog/${article.slug}`
    },
    "keywords": article.tags.join(", ")
  };

  const breadcrumbSchema = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": [
      {
        "@type": "ListItem",
        "position": 1,
        "name": "Início",
        "item": "https://smothmethod.com/"
      },
      {
        "@type": "ListItem",
        "position": 2,
        "name": "Blog",
        "item": "https://smothmethod.com/blog"
      },
      {
        "@type": "ListItem",
        "position": 3,
        "name": article.title,
        "item": `https://smothmethod.com/blog/${article.slug}`
      }
    ]
  };

  // Get previous and next articles for navigation
  const currentIndex = blogArticles.findIndex(a => a.slug === slug);
  const prevArticle = currentIndex > 0 ? blogArticles[currentIndex - 1] : null;
  const nextArticle = currentIndex < blogArticles.length - 1 ? blogArticles[currentIndex + 1] : null;

  return (
    <div className="min-h-screen bg-[#f6f7f9]">
      <SidebarVideoBanners />
      <Helmet>
        <title>{article.metaTitle}</title>
        <meta name="description" content={article.metaDescription} />
        <meta name="robots" content="index, follow" />
        <link rel="canonical" href={`https://smothmethod.com/blog/${article.slug}`} />
        <link rel="alternate" hrefLang="pt-br" href={`https://smothmethod.com/blog/${article.slug}`} />
        <link rel="alternate" hrefLang="x-default" href={`https://smothmethod.com/blog/${article.slug}`} />
        <meta property="og:locale" content="pt_BR" />
        
        {/* Open Graph */}
        <meta property="og:type" content="article" />
        <meta property="og:title" content={article.metaTitle} />
        <meta property="og:description" content={article.metaDescription} />
        <meta property="og:url" content={`https://smothmethod.com/blog/${article.slug}`} />
        <meta property="article:published_time" content={article.publishedAt} />
        <meta property="article:modified_time" content={article.updatedAt} />
        <meta property="article:section" content={article.category} />
        {article.tags.map(tag => (
          <meta key={tag} property="article:tag" content={tag} />
        ))}
        
        {/* Twitter */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={article.metaTitle} />
        <meta name="twitter:description" content={article.metaDescription} />

        <script type="application/ld+json">
          {JSON.stringify(articleSchema)}
        </script>
        <script type="application/ld+json">
          {JSON.stringify(breadcrumbSchema)}
        </script>
      </Helmet>

      <SiteHeader />
      
      <main className="max-w-3xl mx-auto px-4 py-8">
        {/* Breadcrumb */}
        <nav className="mb-6 text-sm" aria-label="Breadcrumb">
          <ol className="flex items-center gap-2 text-gray-500">
            <li>
              <Link to="/" className="hover:text-[#4267B2]">Início</Link>
            </li>
            <li className="mx-1">/</li>
            <li>
              <Link to="/blog" className="hover:text-[#4267B2]">Blog</Link>
            </li>
            <li className="mx-1">/</li>
            <li className="text-gray-700">{article.category}</li>
          </ol>
        </nav>

        {/* Article */}
        <article className="bg-white rounded-lg border border-gray-200 overflow-hidden">
          {/* Header */}
          <header className="p-6 md:p-8 bg-gradient-to-r from-[#4267B2]/5 to-[#4267B2]/10 border-b border-gray-100">
            <div className="flex items-center gap-2 mb-4">
              <span className="text-xs font-medium text-[#4267B2] bg-[#4267B2]/10 px-2 py-1 rounded">
                {article.category}
              </span>
              <span className="text-xs text-gray-500 flex items-center gap-1">
                <Clock className="w-3 h-3" />
                {article.readTime} de leitura
              </span>
            </div>
            
            <h1 className="text-2xl md:text-3xl font-bold text-gray-900 leading-tight mb-4">
              {article.title}
            </h1>
            
            <p className="text-gray-600 mb-4">{article.excerpt}</p>
            
            <div className="flex items-center justify-between">
              <div className="text-sm text-gray-500">
                <span>Publicado em: {article.publishedAt}</span>
                <span className="mx-2">•</span>
                <span>Atualizado em: {article.updatedAt}</span>
              </div>
              <button 
                onClick={() => navigator.share?.({ title: article.title, url: window.location.href })}
                className="flex items-center gap-1 text-sm text-gray-500 hover:text-[#4267B2]"
              >
                <Share2 className="w-4 h-4" />
                Compartilhar
              </button>
            </div>
          </header>

          {/* Content */}
          <div className="p-6 md:p-8">
            <div 
              className="prose prose-lg max-w-none 
                prose-headings:text-gray-900 prose-headings:font-bold
                prose-h2:text-xl prose-h2:mt-8 prose-h2:mb-4
                prose-h3:text-lg prose-h3:mt-6 prose-h3:mb-3
                prose-p:text-gray-700 prose-p:leading-relaxed
                prose-ul:text-gray-700 prose-ol:text-gray-700
                prose-li:my-1
                prose-strong:text-gray-900
                prose-blockquote:border-l-[#4267B2] prose-blockquote:bg-gray-50 prose-blockquote:py-1 prose-blockquote:px-4 prose-blockquote:italic
                prose-table:border prose-table:border-gray-200
                prose-th:bg-gray-50 prose-th:p-3 prose-th:text-left prose-th:font-semibold
                prose-td:p-3 prose-td:border-t prose-td:border-gray-200"
              dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(article.content) }}
            />
          </div>

          {/* Tags */}
          <div className="px-6 md:px-8 pb-6">
            <div className="flex flex-wrap items-center gap-2">
              <Tag className="w-4 h-4 text-gray-400" />
              {article.tags.map(tag => (
                <span 
                  key={tag} 
                  className="text-xs bg-gray-100 text-gray-600 px-2 py-1 rounded"
                >
                  {tag}
                </span>
              ))}
            </div>
          </div>
        </article>

        {/* Article Navigation */}
        <div className="flex gap-4 mt-6">
          {prevArticle ? (
            <Link 
              to={`/blog/${prevArticle.slug}`}
              className="flex-1 bg-white border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow"
            >
              <span className="text-xs text-gray-500 flex items-center gap-1 mb-2">
                <ArrowLeft className="w-3 h-3" /> Anterior
              </span>
              <h4 className="font-medium text-gray-900 text-sm line-clamp-2">
                {prevArticle.title}
              </h4>
            </Link>
          ) : <div className="flex-1" />}
          
          {nextArticle ? (
            <Link 
              to={`/blog/${nextArticle.slug}`}
              className="flex-1 bg-white border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow text-right"
            >
              <span className="text-xs text-gray-500 flex items-center justify-end gap-1 mb-2">
                Próximo <ArrowRight className="w-3 h-3" />
              </span>
              <h4 className="font-medium text-gray-900 text-sm line-clamp-2">
                {nextArticle.title}
              </h4>
            </Link>
          ) : <div className="flex-1" />}
        </div>

        {/* Related Articles */}
        <section className="mt-8">
          <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
            <BookOpen className="w-5 h-5 text-[#4267B2]" />
            Artigos Relacionados
          </h2>
          <div className="space-y-3">
            {relatedArticles.map(related => (
              <Link
                key={related.id}
                to={`/blog/${related.slug}`}
                className="block bg-white border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow"
              >
                <span className="text-xs font-medium text-[#4267B2] bg-[#4267B2]/10 px-2 py-0.5 rounded">
                  {related.category}
                </span>
                <h3 className="font-medium text-gray-900 mt-2 hover:text-[#4267B2]">
                  {related.title}
                </h3>
                <p className="text-sm text-gray-600 mt-1 line-clamp-2">{related.excerpt}</p>
              </Link>
            ))}
          </div>
        </section>

        {/* CTA Section */}
        <section className="mt-8 bg-[#4267B2]/5 border border-[#4267B2]/20 rounded-lg p-6">
          <h3 className="font-semibold text-gray-900 mb-2">
            📚 Quer o Guia Completo?
          </h3>
          <p className="text-sm text-gray-600 mb-4">
            Aprenda tudo sobre PPP, glândulas de Tyson e como identificar o que você tem no nosso guia completo.
          </p>
          <Link 
            to="/guias/pequenas-bolinhas-no-penis"
            className="inline-flex items-center text-[#4267B2] font-medium text-sm hover:underline"
          >
            Ler: Pequenas Bolinhas no P-nis – Guia Completo →
          </Link>
        </section>

        {/* Back to Blog */}
        <div className="mt-8 text-center">
          <Link 
            to="/blog"
            className="inline-flex items-center gap-2 text-[#4267B2] hover:underline"
          >
            <ArrowLeft className="w-4 h-4" />
            Voltar para Todos os Artigos
          </Link>
        </div>

        {/* Footer */}
        <footer className="text-center py-6 mt-8 border-t border-gray-200">
          <p className="text-xs text-gray-500">
            Este artigo é apenas para fins educativos. Sempre consulte um profissional médico em caso de dúvidas de saúde.
          </p>
        </footer>

        <VideoBanner />
      </main>
    </div>
  );
};

export default BlogArticle;
